import { Box, Button, Flex, Heading, Spinner, Table, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { ChartMetric, DailyBarChart, DayPoint } from '../components/DailyBarChart';
import { HostMetrics } from '../types';

const RATE_TOOLTIP =
  'Completed package deliveries per hour of site activity. A 1-minute window counts as active if at least one package_delivery task completed successfully in it. Idle gaps are excluded. Bulk-created package rows are not counted until delivery completes.';

const PACKAGES_PER_DAY_TOOLTIP =
  'Successfully completed package_delivery tasks per calendar day, averaged over the full window. Days with no deliveries count as zero.';

const DEFAULT_WINDOW_DAYS = 30;

type SiteDayRow = {
  host: string;
  site: string;
  byDay: Map<string, { packages: number; activeBuckets: number }>;
  totalPackages: number;
  totalBuckets: number;
  hasRateData: boolean;
};

type SortKey = 'site' | 'rate' | 'packagesPerDay' | 'total';
type SortDir = 'asc' | 'desc';
type ChartSelection = {
  host: string;
  site: string;
  metric: ChartMetric;
};

function emptyRow(host: string, site: string): SiteDayRow {
  return {
    host,
    site,
    byDay: new Map(),
    totalPackages: 0,
    totalBuckets: 0,
    hasRateData: false,
  };
}

function packagesPerHour(packages: number, activeBuckets: number, hasRateData: boolean): number | null {
  if (!hasRateData || activeBuckets <= 0) {
    return null;
  }
  return (packages * 60) / activeBuckets;
}

function utcDay(iso: string | null): string | null {
  if (!iso) {
    return null;
  }
  const time = Date.parse(iso);
  if (Number.isNaN(time)) {
    return null;
  }
  return new Date(time).toISOString().slice(0, 10);
}

function addDays(day: string, delta: number): string {
  const [year, month, date] = day.split('-').map(Number);
  const utc = new Date(Date.UTC(year, (month || 1) - 1, date || 1));
  utc.setUTCDate(utc.getUTCDate() + delta);
  return utc.toISOString().slice(0, 10);
}

function dayRange(end: string, count: number): string[] {
  const days: string[] = [];
  for (let offset = count - 1; offset >= 0; offset -= 1) {
    days.push(addDays(end, -offset));
  }
  return days;
}

function buildTable(hosts: HostMetrics[]): {
  axis: string[];
  windowDays: number;
  rows: SiteDayRow[];
  fetchedAt: string | null;
} {
  const byKey = new Map<string, SiteDayRow>();
  let fetchedAt: string | null = null;
  let latestDay: string | null = null;
  let windowDays = 0;

  for (const host of hosts) {
    if (host.fetchedAt && (!fetchedAt || host.fetchedAt > fetchedAt)) {
      fetchedAt = host.fetchedAt;
    }
    if (typeof host.days === 'number' && host.days > windowDays) {
      windowDays = host.days;
    }
    const siteNames = new Set<string>(host.siteNames ?? []);
    for (const row of host.daily) {
      if (row.site_name) {
        siteNames.add(row.site_name);
      }
    }
    for (const site of siteNames) {
      const key = `${host.name}\0${site}`;
      if (!byKey.has(key)) {
        byKey.set(key, emptyRow(host.name, site));
      }
    }
    for (const row of host.daily) {
      const site = row.site_name ?? '(no site)';
      const key = `${host.name}\0${site}`;
      const existing = byKey.get(key) ?? emptyRow(host.name, site);
      const packages = row.packages || 0;
      const hasBuckets = row.active_buckets !== undefined || row.rate !== undefined;
      const activeBuckets = row.active_buckets || 0;
      const prior = existing.byDay.get(row.day) ?? { packages: 0, activeBuckets: 0 };
      existing.byDay.set(row.day, {
        packages: prior.packages + packages,
        activeBuckets: prior.activeBuckets + activeBuckets,
      });
      existing.totalPackages += packages;
      existing.totalBuckets += activeBuckets;
      existing.hasRateData = existing.hasRateData || hasBuckets;
      byKey.set(key, existing);
      if (!latestDay || row.day > latestDay) {
        latestDay = row.day;
      }
    }
  }

  if (windowDays <= 0) {
    windowDays = DEFAULT_WINDOW_DAYS;
  }
  const endCandidates = [latestDay, utcDay(fetchedAt)].filter((day): day is string => Boolean(day)).sort();
  const end = endCandidates[endCandidates.length - 1];
  const axis = dayRange(end ?? new Date().toISOString().slice(0, 10), windowDays);
  return { axis, windowDays, rows: [...byKey.values()], fetchedAt };
}

function sortValue(row: SiteDayRow, key: SortKey, windowDays: number): string | number | null {
  if (key === 'site') {
    return `${row.site}\0${row.host}`;
  }
  if (key === 'rate') {
    return packagesPerHour(row.totalPackages, row.totalBuckets, row.hasRateData);
  }
  if (key === 'packagesPerDay') {
    return windowDays > 0 ? row.totalPackages / windowDays : 0;
  }
  return row.totalPackages;
}

function compareRows(a: SiteDayRow, b: SiteDayRow, key: SortKey, dir: SortDir, windowDays: number): number {
  const left = sortValue(a, key, windowDays);
  const right = sortValue(b, key, windowDays);
  const empty = dir === 'asc' ? 1 : -1;
  if (left === null && right === null) {
    return a.site.localeCompare(b.site) || a.host.localeCompare(b.host);
  }
  if (left === null) {
    return empty;
  }
  if (right === null) {
    return -empty;
  }
  const cmp = typeof left === 'string' ? left.localeCompare(right as string) : left - (right as number);
  if (cmp !== 0) {
    return dir === 'asc' ? cmp : -cmp;
  }
  return a.site.localeCompare(b.site) || a.host.localeCompare(b.host);
}

function formatCount(value: number): string {
  return value.toLocaleString();
}

function formatRate(rate: number | null, hasRateData: boolean): string {
  if (!hasRateData) {
    return 'n/a';
  }
  if (rate === null || !Number.isFinite(rate)) {
    return 'n/a';
  }
  return `${rate.toLocaleString(undefined, { maximumFractionDigits: 1 })}/h`;
}

function formatAverage(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function sameSelection(current: ChartSelection | null, host: string, site: string, metric: ChartMetric): boolean {
  return current?.host === host && current.site === site && current.metric === metric;
}

function pointsFor(rows: SiteDayRow[], host: string, site: string, metric: ChartMetric, axis: string[]): DayPoint[] {
  const selected = host === '' && site === '' ? rows : rows.filter(row => row.host === host && row.site === site);
  return axis.map(day => {
    let packages = 0;
    let buckets = 0;
    let hasRate = false;
    for (const row of selected) {
      const cell = row.byDay.get(day);
      packages += cell?.packages ?? 0;
      buckets += cell?.activeBuckets ?? 0;
      hasRate = hasRate || row.hasRateData;
    }
    const rate = packagesPerHour(packages, buckets, hasRate);
    return {
      day,
      value: metric === 'packages' ? packages : (rate ?? 0),
      packagesLabel: `${formatCount(packages)} packages`,
      rateLabel: rate === null ? 'No activity' : formatRate(rate, true),
    };
  });
}

function chartTitle(selection: ChartSelection, rows: SiteDayRow[]): string {
  if (selection.host === '' && selection.site === '') {
    return 'All sites';
  }
  const row = rows.find(item => item.host === selection.host && item.site === selection.site);
  const site = row?.site ?? selection.site;
  const host = row?.host ?? selection.host;
  return `${site} (${host})`;
}

function SortableHeader({
  label,
  sortKey,
  current,
  dir,
  onSort,
  title,
  textAlign = 'start',
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  title?: string;
  textAlign?: 'start' | 'end';
}) {
  const active = current === sortKey;
  return (
    <Table.ColumnHeader
      textAlign={textAlign}
      whiteSpace='nowrap'
      cursor='pointer'
      userSelect='none'
      fontWeight='medium'
      _hover={{ bg: 'transparent', fontWeight: 'medium' }}
      onClick={() => onSort(sortKey)}
      title={title}
    >
      {label}
      <Box as='span' display='inline-block' w='0.9em' textAlign='start'>
        {active ? (dir === 'asc' ? '↑' : '↓') : ''}
      </Box>
    </Table.ColumnHeader>
  );
}

function MetricButton({
  active,
  title,
  onClick,
  children,
}: {
  active: boolean;
  title: string;
  onClick: () => void;
  children: string;
}) {
  return (
    <Button
      variant='plain'
      type='button'
      display='block'
      w='full'
      h='auto'
      minW={0}
      px={0}
      py={0}
      textAlign='end'
      fontVariantNumeric='tabular-nums'
      fontWeight='inherit'
      color={active ? 'primary.fg' : 'inherit'}
      textDecoration={active ? 'underline' : undefined}
      aria-pressed={active}
      title={title}
      _hover={{ color: 'primary.fg', textDecoration: 'underline' }}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

export default function Metrics() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['metrics'],
    queryFn: api.metrics,
    refetchInterval: false,
  });
  const [sortKey, setSortKey] = useState<SortKey>('site');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selection, setSelection] = useState<ChartSelection | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  const { axis, windowDays, rows, fetchedAt } = useMemo(() => buildTable(data?.hosts ?? []), [data]);
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => compareRows(a, b, sortKey, sortDir, windowDays)),
    [rows, sortKey, sortDir, windowDays]
  );

  const openSite = selection ? `${selection.host}\0${selection.site}` : null;
  useEffect(() => {
    if (openSite) {
      chartRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [openSite]);

  const onSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(current => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'site' ? 'asc' : 'desc');
  };

  const toggleChart = (host: string, site: string, metric: ChartMetric) => {
    setSelection(current => (sameSelection(current, host, site, metric) ? null : { host, site, metric }));
  };

  if (isLoading) {
    return <Spinner />;
  }
  if (error) {
    return <Text color='red.500'>Failed to load metrics: {(error as Error).message}</Text>;
  }

  const grandTotal = sortedRows.reduce((sum, row) => sum + row.totalPackages, 0);
  const grandBuckets = sortedRows.reduce((sum, row) => sum + row.totalBuckets, 0);
  const hasAnyRateData = sortedRows.some(row => row.hasRateData);
  const chartPoints = selection ? pointsFor(sortedRows, selection.host, selection.site, selection.metric, axis) : [];
  const totalLabel = `Total (${windowDays} days)`;

  return (
    <Flex direction='column' gap={4}>
      <Heading size='xl'>Packages per site</Heading>
      <Text fontSize='sm' color='gray.500'>
        Last {windowDays} days. Recalculated once per day.
        {fetchedAt ? ` Last refreshed ${new Date(fetchedAt).toLocaleString()}.` : ''} Click a rate or pkgs/day value to
        chart that site by day. Click a column header to sort.
      </Text>
      {sortedRows.length > 0 ? (
        <Box borderWidth='1px' borderColor='card.border' borderRadius='lg' overflow='auto'>
          <Table.Root size='sm' variant='line' w='full'>
            <Table.Header>
              <Table.Row>
                <SortableHeader label='Site' sortKey='site' current={sortKey} dir={sortDir} onSort={onSort} />
                <Table.ColumnHeader minW='8rem'>Host</Table.ColumnHeader>
                <SortableHeader
                  label='Rate'
                  sortKey='rate'
                  current={sortKey}
                  dir={sortDir}
                  onSort={onSort}
                  title={RATE_TOOLTIP}
                  textAlign='end'
                />
                <SortableHeader
                  label='Pkgs/day'
                  sortKey='packagesPerDay'
                  current={sortKey}
                  dir={sortDir}
                  onSort={onSort}
                  title={PACKAGES_PER_DAY_TOOLTIP}
                  textAlign='end'
                />
                <SortableHeader
                  label={totalLabel}
                  sortKey='total'
                  current={sortKey}
                  dir={sortDir}
                  onSort={onSort}
                  textAlign='end'
                />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sortedRows.map(row => {
                const rate = packagesPerHour(row.totalPackages, row.totalBuckets, row.hasRateData);
                const packagesPerDay = windowDays > 0 ? row.totalPackages / windowDays : 0;
                const rateOpen = sameSelection(selection, row.host, row.site, 'rate');
                const packagesOpen = sameSelection(selection, row.host, row.site, 'packages');
                return (
                  <Table.Row
                    key={`${row.host}:${row.site}`}
                    bg={rateOpen || packagesOpen ? 'primary.subtle' : undefined}
                  >
                    <Table.Cell fontWeight='medium' whiteSpace='nowrap'>
                      {row.site}
                    </Table.Cell>
                    <Table.Cell whiteSpace='nowrap'>{row.host}</Table.Cell>
                    <Table.Cell textAlign='end'>
                      <MetricButton
                        active={rateOpen}
                        title={`${RATE_TOOLTIP} Click to chart rate by day.`}
                        onClick={() => toggleChart(row.host, row.site, 'rate')}
                      >
                        {formatRate(rate, row.hasRateData)}
                      </MetricButton>
                    </Table.Cell>
                    <Table.Cell textAlign='end'>
                      <MetricButton
                        active={packagesOpen}
                        title={`${PACKAGES_PER_DAY_TOOLTIP} Click to chart packages by day.`}
                        onClick={() => toggleChart(row.host, row.site, 'packages')}
                      >
                        {formatAverage(packagesPerDay)}
                      </MetricButton>
                    </Table.Cell>
                    <Table.Cell textAlign='end' fontWeight='medium' fontVariantNumeric='tabular-nums'>
                      {formatCount(row.totalPackages)}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
              <Table.Row bg={selection?.host === '' && selection.site === '' ? 'primary.subtle' : undefined}>
                <Table.Cell fontWeight='bold'>All sites</Table.Cell>
                <Table.Cell />
                <Table.Cell textAlign='end' fontWeight='bold'>
                  <MetricButton
                    active={sameSelection(selection, '', '', 'rate')}
                    title={`${RATE_TOOLTIP} Click to chart rate by day.`}
                    onClick={() => toggleChart('', '', 'rate')}
                  >
                    {formatRate(packagesPerHour(grandTotal, grandBuckets, hasAnyRateData), hasAnyRateData)}
                  </MetricButton>
                </Table.Cell>
                <Table.Cell textAlign='end' fontWeight='bold'>
                  <MetricButton
                    active={sameSelection(selection, '', '', 'packages')}
                    title={`${PACKAGES_PER_DAY_TOOLTIP} Click to chart packages by day.`}
                    onClick={() => toggleChart('', '', 'packages')}
                  >
                    {formatAverage(windowDays > 0 ? grandTotal / windowDays : 0)}
                  </MetricButton>
                </Table.Cell>
                <Table.Cell textAlign='end' fontWeight='bold' fontVariantNumeric='tabular-nums'>
                  {formatCount(grandTotal)}
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table.Root>
        </Box>
      ) : (
        <Text color='gray.500'>No hosts or sites reported yet.</Text>
      )}
      {selection ? (
        <Box ref={chartRef} borderWidth='1px' borderColor='card.border' borderRadius='lg' bg='card.bg' p={4}>
          <DailyBarChart
            title={chartTitle(selection, sortedRows)}
            points={chartPoints}
            metric={selection.metric}
            onMetricChange={metric => setSelection(current => (current ? { ...current, metric } : current))}
          />
        </Box>
      ) : null}
      {(data?.hosts ?? [])
        .filter(host => host.error)
        .map(host => (
          <Text key={host.name} fontSize='sm' color='orange.500'>
            {host.name}: metrics unavailable ({host.error})
          </Text>
        ))}
    </Flex>
  );
}
